import { z } from 'zod/v4';
import { USER_DEVELOPMENT_TAGS } from '../../../constants.js';

export const AdminUserUpdateParamsSchema = z.object({
  id: z.string().min(1),
});

export type AdminUserUpdateParams = z.infer<typeof AdminUserUpdateParamsSchema>;

/**
 * Mirrors the legacy PUT `/v3/admin/users/:id` route
 * (`backend/src/2025-06/api-service/routes/http/v3/admin/users/update.js`),
 * which declares no body schema at all — any property the caller sends
 * passes straight to `usersService.updateUnscoped`. `langId` and
 * `developmentTag` therefore aren't validated any more strictly than the
 * legacy route did; unknown properties (the FE also sends a `companyId` that
 * `updateUnscoped` never reads) are silently stripped by zod's default object
 * behaviour, matching that the legacy route ignored them too.
 */
export const AdminUserUpdateBodySchema = z.object({
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  langId: z.string().nullable().optional(),
  isBlocked: z.boolean().optional(),
  developmentTag: z.enum(USER_DEVELOPMENT_TAGS).nullable().optional(),
});

export type AdminUserUpdateBody = z.infer<typeof AdminUserUpdateBodySchema>;

const EnabledDevelopmentTagSchema = z.object({
  featureId: z.string(),
  tag: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isEnabled: z.number(),
});

/**
 * Row shape returned by the admin full-update-user endpoint. Same
 * pass-through shape as `AdminUserPatchResponseSchema` (bare row +
 * `enabledDevelopmentTags`) — the legacy `updateUnscoped` this ports builds
 * its response identically to `patchUnscoped`'s legacy predecessor.
 */
export const AdminUserUpdateResponseSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  primaryCompanyId: z.string().nullable(),
  langId: z.string().nullable(),
  isBlocked: z.boolean(),
  developmentTag: z.string().nullable(),
  avatarFileUrl: z.string().nullable(),
  originalAvatarFileUrl: z.string().nullable(),
  enabledDevelopmentTags: z.array(EnabledDevelopmentTagSchema),
  updatedAt: z.union([z.string(), z.date()]).nullable(),
});

export type AdminUserUpdateResponse = z.infer<
  typeof AdminUserUpdateResponseSchema
>;
